import { HeaderPage } from "@/components/HeaderPage";
import { CardContent } from "@/components/CardContent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Search, Plus, Eye, Trash2 } from "lucide-react";
import { Table } from "@/components/Table";
import type { Sector } from "@/types/Sector";
import { useDeleteSector, useGetSectors } from "@/hooks/useSectors";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { MdOutlineEdit } from "react-icons/md";
import { Loading } from "@/components/Loading";
import { useState, useEffect } from "react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

const statusConfig: Record<
  string,
  {
    label: string;
    variant: "destructive" | "success";
  }
> = {
  ACTIVE: { label: "Ativo", variant: "success" },
  INACTIVE: { label: "Inativo", variant: "destructive" },
};

export const Setores = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [status, setStatus] = useState<string>("all");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sectorToDelete, setSectorToDelete] = useState<string | null>(null);
  const limit = 10;

  const queryClient = useQueryClient();
  const { data: sectors, isLoading } = useGetSectors({
    page: currentPage,
    limit,
    status: status !== "all" ? status : undefined,
    search: debouncedSearch || undefined,
  });
  const navigate = useNavigate();
  const deleteSector = useDeleteSector();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleStatusChange = (value: string) => {
    setStatus(value);
    setCurrentPage(1);
  };

  const handleDeleteClick = (id: string) => {
    setSectorToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!sectorToDelete) return;

    try {
      await deleteSector.mutateAsync(sectorToDelete);
      toast.success("Setor deletado com sucesso!");

      // Invalidar cache para atualizar a lista
      queryClient.invalidateQueries({ queryKey: ["getSectors"] });

      setSectorToDelete(null);
    } catch (error: any) {
      console.error("Erro ao deletar setor:", error);
      const errorMessage =
        error.response?.data?.message || "Erro ao deletar setor";
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <HeaderPage
        title="Setores"
        description="Gerencie os setores e suas equipes"
      />

      <Card className="mb-6 mt-6 bg-white">
        <CardContentUI className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar setores..."
                className="pl-10 h-10 text-sm"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <div className="flex gap-3">
              <Select value={status} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-45 h-10 text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="ACTIVE">Ativos</SelectItem>
                  <SelectItem value="INACTIVE">Inativos</SelectItem>
                </SelectContent>
              </Select>
              <Button
                className="h-10 text-sm px-4 text-white cursor-pointer bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]"
                onClick={() => navigate("/sectors/create")}
              >
                <Plus className="h-4 w-4 mr-2" />
                Novo Setor
              </Button>
            </div>
          </div>
        </CardContentUI>
      </Card>

      <CardContent
        title="Lista de Setores"
        content={
          isLoading ? (
            <Loading label="Carregando setores.." />
          ) : (
            <Table<Sector>
              columns={[
                { key: "id", label: "Id" },
                { key: "name", label: "Nome" },
                { key: "description", label: "Descrição" },
                { key: "acronym", label: "Sigla" },
                { key: "email", label: "E-mail" },
                {
                  key: "phone",
                  label: "Telefone",
                  render: (item) => {
                    return (
                      <span>
                        {item.phone
                          .replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3")
                          .replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3")}
                      </span>
                    );
                  },
                },
                { key: "workingHoursStart", label: "Início do expediente" },
                { key: "workingHoursEnd", label: "Fim do expediente" },
                {
                  key: "status",
                  label: "Status",
                  render: (item) => {
                    const config = statusConfig[item.status] || {
                      label: item.status,
                      variant: "outline" as const,
                    };
                    return (
                      <Badge variant={config.variant}>{config.label}</Badge>
                    );
                  },
                },
                {
                  key: "actions",
                  label: "Ações",
                  render: (item) => {
                    return (
                      <div className="flex items-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer hover:bg-gray-100 transition-colors"
                          title="Visualizar"
                          onClick={() => navigate(`/sectors/view/${item.id}`)}
                        >
                          <Eye className="h-5 w-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer hover:bg-gray-100 transition-colors"
                          title="Editar"
                          onClick={() => navigate(`/sectors/edit/${item.id}`)}
                        >
                          <MdOutlineEdit className="h-5 w-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 cursor-pointer hover:bg-red-50 transition-colors"
                          title="Deletar"
                          onClick={() => handleDeleteClick(item.id)}
                        >
                          <Trash2 className="h-6 w-6" />
                        </Button>
                      </div>
                    );
                  },
                },
              ]}
              data={sectors?.data || []}
              pagination={sectors?.pagination}
              onPageChange={handlePageChange}
            />
          )
        }
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Confirmar Deleção"
        description="Tem certeza que deseja deletar este setor? Esta ação não pode ser desfeita."
        confirmText="Deletar"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        variant="destructive"
        isLoading={deleteSector.isPending}
      />
    </>
  );
};
